import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  MoneyInput,
  StyledSelect,
  type StyledSelectOption,
  toast,
} from '@jumpstone/ui-react';
import {
  fetchPaymentSourceAccounts,
  type ListPaymentSourceAccountsResult,
  type RealPaymentSourceAccountRow,
} from '../../lib/auth/oauth_pay_run_post_actions';
import { useOAuthEntityContext } from '../../lib/auth/oauth_entity_context';
import type {
  PesangonTerminationReason,
  PostGratuityTerminationInput,
  PostPesangonInput,
  TerminateEmployeeInput,
  TerminationMutationFailure,
  UseTerminationResult,
} from '../../data/use_termination';
import type { EmployeeJurisdiction } from './employee_form_validation';
import type { TerminationDraft } from './terminate_confirm_dialog';
import { build_bank_account_options } from '../payroll/post_run_select_options';

/**
 * Final-pay drawer (#23313). Renders the jurisdiction-appropriate branch:
 *
 * - `LK` — LK gratuity settlement. Reads the entity-level gratuity provision
 *   preview via `getGratuityProvision`, collects YTD taxable remuneration
 *   and a bank-account picker, posts `postGratuityTermination` FIRST
 *   (creates the settlement journal), then `terminateEmployee` to flip the
 *   status. This ordering matches the manual flow — provisions must be
 *   released off the GL before the employee record becomes inactive, or
 *   the mandatory `getEmployeeGratuityProvisionCents` snapshot lookup can
 *   race with the status flip.
 * - `ID` — Indonesia pesangon. Collects termination_reason plus UPH cash
 *   components (unused_leave, repatriation, other, separation_pay), posts
 *   `postPesangon` (which flips the employee status itself — no separate
 *   `terminateEmployee` call needed).
 * - `OTHER` — no jurisdiction-specific settlement. Only the plain
 *   `terminateEmployee` call. Rendered as a confirmation summary; the
 *   confirm dialog's own path could also be used here — but keeping the
 *   drawer for all three jurisdictions lets the profile screen use a
 *   single "next step after confirm" affordance regardless of jurisdiction.
 *
 * Money is always integer minor units per docs/money-rules.md — MoneyInput
 * enforces that at the input boundary. Never parseFloat here.
 *
 * Read-only-grant gating: this drawer is unreachable when the OAuth grant
 * isn't write-capable — the Terminate button in employee_profile_screen.tsx
 * is hidden entirely when `is_read_only_grant`. `settle_gratuity` /
 * `post_pesangon` / `terminate` from `useTermination()` all become
 * `undefined` in that state; this drawer treats an `undefined` handler as
 * an unreachable-state assertion, not a runtime option, since the screen
 * cannot open it without a write-capable grant.
 *
 * `postGratuityAccrual` (period-level provision accrual) is intentionally
 * NOT surfaced here — it is not a per-termination action. It is
 * allowlisted server-side (#23313) so the surface is complete, but the
 * accrual UI belongs on a separate period-end panel not scoped to this
 * flow.
 */
export interface FinalPayDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jurisdiction: EmployeeJurisdiction;
  /**
   * An independent contractor is not an employee: no gratuity, pesangon or
   * leave payout applies (the API refuses them with
   * `LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE`), so the drawer offers only the
   * plain terminate branch with a contractor-specific note.
   */
  is_contractor: boolean;
  employee_id: string;
  employee_name: string;
  /** Termination_date + reason carried over from the confirm dialog. */
  draft: TerminationDraft;
  actions: UseTerminationResult;
  onSuccess: () => void;
}

function format_cents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * The 24 pesangon reason keys the backend accepts (`PESANGON_REASONS` in
 * `packages/jumpstone-core/src/jurisdictions/id/payroll/pesangon.ts`).
 * Duplicated here as a plain literal list — the drawer's reason `StyledSelect`
 * maps over it and only needs the KEYS, not the full multiplier/article table.
 * Kept in one place so a drift catches at type check via the union
 * intersection with `PesangonTerminationReason`.
 */
const PESANGON_REASON_OPTIONS = [
  'merger_acquisition',
  'takeover',
  'takeover_worker_refuses_changed_terms',
  'efficiency_due_to_losses',
  'efficiency_to_prevent_losses',
  'closure_due_to_losses',
  'closure_not_due_to_losses',
  'closure_force_majeure',
  'force_majeure_no_closure',
  'pkpu_due_to_losses',
  'pkpu_not_due_to_losses',
  'bankruptcy',
  'worker_requested_employer_wrongdoing',
  'court_found_no_employer_wrongdoing',
  'resignation',
  'absence_5_days',
  'violation_after_warnings',
  'urgent_violation',
  'detained_company_loss',
  'detained_not_company_loss',
  'prolonged_illness_or_disability',
  'retirement',
  'death',
] as const satisfies readonly PesangonTerminationReason[];

/**
 * Renders the localized label for a pesangon reason. Uses a switch of
 * literal `t('…')` calls (rather than a dynamic key lookup off a map)
 * because the `ledger/no-orphaned-i18n-key` ESLint rule and the i18n
 * extractor both parse only STATIC string keys — a template-literal or
 * indirected-map lookup would flag every one of these 24 keys as an
 * orphan even though they are all actually referenced here.
 */
function pesangon_reason_label(
  t: (key: string) => string,
  r: PesangonTerminationReason,
): string {
  switch (r) {
    case 'merger_acquisition':
      return t('employees.final_pay.id_reason.merger_acquisition');
    case 'takeover':
      return t('employees.final_pay.id_reason.takeover');
    case 'takeover_worker_refuses_changed_terms':
      return t(
        'employees.final_pay.id_reason.takeover_worker_refuses_changed_terms',
      );
    case 'efficiency_due_to_losses':
      return t('employees.final_pay.id_reason.efficiency_due_to_losses');
    case 'efficiency_to_prevent_losses':
      return t('employees.final_pay.id_reason.efficiency_to_prevent_losses');
    case 'closure_due_to_losses':
      return t('employees.final_pay.id_reason.closure_due_to_losses');
    case 'closure_not_due_to_losses':
      return t('employees.final_pay.id_reason.closure_not_due_to_losses');
    case 'closure_force_majeure':
      return t('employees.final_pay.id_reason.closure_force_majeure');
    case 'force_majeure_no_closure':
      return t('employees.final_pay.id_reason.force_majeure_no_closure');
    case 'pkpu_due_to_losses':
      return t('employees.final_pay.id_reason.pkpu_due_to_losses');
    case 'pkpu_not_due_to_losses':
      return t('employees.final_pay.id_reason.pkpu_not_due_to_losses');
    case 'bankruptcy':
      return t('employees.final_pay.id_reason.bankruptcy');
    case 'worker_requested_employer_wrongdoing':
      return t(
        'employees.final_pay.id_reason.worker_requested_employer_wrongdoing',
      );
    case 'court_found_no_employer_wrongdoing':
      return t(
        'employees.final_pay.id_reason.court_found_no_employer_wrongdoing',
      );
    case 'resignation':
      return t('employees.final_pay.id_reason.resignation');
    case 'absence_5_days':
      return t('employees.final_pay.id_reason.absence_5_days');
    case 'violation_after_warnings':
      return t('employees.final_pay.id_reason.violation_after_warnings');
    case 'urgent_violation':
      return t('employees.final_pay.id_reason.urgent_violation');
    case 'detained_company_loss':
      return t('employees.final_pay.id_reason.detained_company_loss');
    case 'detained_not_company_loss':
      return t('employees.final_pay.id_reason.detained_not_company_loss');
    case 'prolonged_illness_or_disability':
      return t('employees.final_pay.id_reason.prolonged_illness_or_disability');
    case 'retirement':
      return t('employees.final_pay.id_reason.retirement');
    case 'death':
      return t('employees.final_pay.id_reason.death');
  }
}

export function FinalPayDrawer({
  open,
  onOpenChange,
  jurisdiction,
  is_contractor,
  employee_id,
  employee_name,
  draft,
  actions,
  onSuccess,
}: FinalPayDrawerProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  // A contractor never gets a statutory settlement branch.
  const branch: EmployeeJurisdiction | 'CONTRACTOR' = is_contractor
    ? 'CONTRACTOR'
    : jurisdiction;

  return (
    <Drawer
      open={open}
      onClose={() => {
        onOpenChange(false);
      }}
      title={t('employees.final_pay.drawer_title', { name: employee_name })}
    >
      {branch === 'LK' && (
        <LkGratuityBranch
          employee_id={employee_id}
          employee_name={employee_name}
          draft={draft}
          actions={actions}
          onSuccess={() => {
            onOpenChange(false);
            onSuccess();
          }}
        />
      )}
      {branch === 'ID' && (
        <IdPesangonBranch
          employee_id={employee_id}
          employee_name={employee_name}
          draft={draft}
          actions={actions}
          onSuccess={() => {
            onOpenChange(false);
            onSuccess();
          }}
        />
      )}
      {(branch === 'OTHER' || branch === 'CONTRACTOR') && (
        <OtherJurisdictionBranch
          is_contractor={branch === 'CONTRACTOR'}
          employee_id={employee_id}
          employee_name={employee_name}
          draft={draft}
          actions={actions}
          onSuccess={() => {
            onOpenChange(false);
            onSuccess();
          }}
        />
      )}
    </Drawer>
  );
}

// ── LK gratuity settlement branch ─────────────────────────────────────────

interface BranchProps {
  employee_id: string;
  employee_name: string;
  draft: TerminationDraft;
  actions: UseTerminationResult;
  onSuccess: () => void;
}

function LkGratuityBranch({
  employee_id,
  employee_name,
  draft,
  actions,
  onSuccess,
}: BranchProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const entityContext = useOAuthEntityContext();
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;

  const [ytd_taxable_remuneration_cents, set_ytd_cents] = React.useState(0);
  const [scheme_approved, set_scheme_approved] = React.useState(false);
  const [bank_account_id, set_bank_account_id] = React.useState<string | null>(
    null,
  );
  const [bank_accounts, set_bank_accounts] = React.useState<
    RealPaymentSourceAccountRow[]
  >([]);
  const [bank_accounts_loaded, set_bank_accounts_loaded] =
    React.useState(false);
  const [failure, set_failure] =
    React.useState<TerminationMutationFailure | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);
  const [provision, set_provision] = React.useState<{
    applicable: boolean;
    existing_provision_cents: number;
    headcount: number;
  } | null>(null);
  const [provision_error, set_provision_error] = React.useState(false);
  // Latched once `postGratuityTermination` has succeeded, so a retry of
  // this drawer's action after a `terminateEmployee` failure skips the
  // settlement leg (adversarial-review fix — `postGratuityTermination`
  // has no `is_active` guard, and its per-employee provision snapshot is
  // cleared to zero on the first successful call, so a naive retry would
  // re-post a second termination journal with `pnlTrueUpCents` inflated
  // to the FULL statutory amount).
  const [settlement_posted, set_settlement_posted] = React.useState(false);

  // Fetch the provision preview + bank accounts once, when the branch
  // mounts. Both are read-only queries and reachable to any `payroll:
  // employer:read` grant — the drawer's write CTA below stays disabled
  // until both resolve.
  //
  React.useEffect(() => {
    // `cancelled` is boxed in a wrapper object and read through a helper
    // (`isCancelled`) so eslint-typescript's `no-unnecessary-condition`
    // flow analysis can't prove it stays false — same escape hatch as
    // `use_employees.ts`'s `() => cancelled` callback pass-through. The
    // check IS meaningful: the cleanup on line-below sets the flag when
    // the effect re-runs (e.g. `termination_date` changed), and a stale
    // state update from the previous effect run must not clobber a
    // freshly-fetched provision for the new date.
    const box: { cancelled: boolean } = { cancelled: false };
    const isCancelled = (): boolean => box.cancelled;
    async function load(): Promise<void> {
      if (!actions.fetch_gratuity_provision || !readyEntity) return;
      const result = await actions.fetch_gratuity_provision({
        as_of_date: draft.termination_date,
      });
      if (isCancelled()) return;
      if (result.outcome === 'failure') {
        set_provision_error(true);
        return;
      }
      set_provision({
        applicable: result.value.applicable,
        existing_provision_cents: result.value.existing_provision_cents,
        headcount: result.value.headcount,
      });
      const bank: ListPaymentSourceAccountsResult =
        await fetchPaymentSourceAccounts({
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
      if (isCancelled()) return;
      if (bank.ok) {
        set_bank_accounts(bank.accounts);
      }
      set_bank_accounts_loaded(true);
    }
    void load();
    return () => {
      box.cancelled = true;
    };
  }, [actions.fetch_gratuity_provision, draft.termination_date, readyEntity]);

  async function handle_settle(): Promise<void> {
    set_failure(null);
    if (!actions.settle_gratuity || !actions.terminate || !bank_account_id) {
      return;
    }
    set_is_submitting(true);
    // Skip the settlement leg on retry — `postGratuityTermination` has no
    // `is_active` idempotency guard, and its per-employee provision
    // snapshot is cleared on the first successful call, so re-running it
    // would post a SECOND termination journal for the full statutory
    // amount. `terminateEmployee` alone is safe to retry (its "already
    // terminated" guard makes it a no-op after the first success).
    if (!settlement_posted) {
      const settle_input: PostGratuityTerminationInput = {
        employee_id,
        date: draft.termination_date,
        bank_account_id,
        ytd_taxable_remuneration_cents,
        scheme_approved,
      };
      const settle_result = await actions.settle_gratuity(settle_input);
      if (settle_result.outcome === 'failure') {
        set_is_submitting(false);
        set_failure(settle_result.failure);
        return;
      }
      set_settlement_posted(true);
    }
    const terminate_input: TerminateEmployeeInput = {
      employee_id,
      termination_date: draft.termination_date,
      reason: draft.reason,
    };
    const terminate_result = await actions.terminate(terminate_input);
    set_is_submitting(false);
    if (terminate_result.outcome === 'failure') {
      // The settlement journal HAS posted (`settlement_posted` is now
      // true). Surface the specific failure — the button click on retry
      // will re-run only `terminateEmployee` (the guard above skips the
      // already-posted settle leg), and the "already terminated" case is
      // handled as a warning (not a red banner) because it means an
      // earlier `terminateEmployee` actually did land — the network read
      // just didn't see the ok.
      set_failure(terminate_result.failure);
      return;
    }
    toast.success(
      t('employees.final_pay.lk_settle_success', { name: employee_name }),
    );
    onSuccess();
  }

  const can_submit =
    provision !== null &&
    bank_account_id !== null &&
    bank_accounts_loaded &&
    !is_submitting;

  return (
    <div className="space-y-4">
      <div className="text-sm text-[color:var(--ink-muted)]">
        {t('employees.final_pay.lk_gratuity_heading')}
      </div>
      {provision_error ? (
        <Alert variant="danger" data-testid="final-pay-provision-error">
          {t('employees.final_pay.lk_provision_error')}
        </Alert>
      ) : provision === null ? (
        <div className="nv-skel" style={{ height: 44 }} />
      ) : (
        <div className="glass-soft rounded-md px-4 py-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-[color:var(--ink-muted)]">
              {t('employees.final_pay.lk_headcount_label')}
            </span>
            <span className="num">{provision.headcount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[color:var(--ink-muted)]">
              {t('employees.final_pay.lk_existing_provision_label')}
            </span>
            <span className="num">
              {format_cents(provision.existing_provision_cents)}
            </span>
          </div>
          {!provision.applicable && (
            <p className="text-[var(--color-warning-text)] text-xs pt-1">
              {t('employees.final_pay.lk_not_applicable')}
            </p>
          )}
        </div>
      )}

      <div className="space-y-1">
        <label htmlFor="final-pay-ytd-remuneration" className="nv-label">
          {t('employees.final_pay.lk_ytd_remuneration_label')}
        </label>
        <MoneyInput
          id="final-pay-ytd-remuneration"
          className="w-full"
          value={ytd_taxable_remuneration_cents}
          onChange={set_ytd_cents}
        />
        <p className="nv-help">
          {t('employees.final_pay.lk_ytd_remuneration_help')}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="final-pay-scheme-approved"
          checked={scheme_approved}
          onCheckedChange={(checked) => {
            set_scheme_approved(checked);
          }}
        />
        <label htmlFor="final-pay-scheme-approved" className="text-sm">
          {t('employees.final_pay.lk_scheme_approved_label')}
        </label>
      </div>

      <div className="space-y-1">
        <label htmlFor="final-pay-bank-account" className="nv-label">
          {t('employees.final_pay.lk_bank_account_label')}
        </label>
        {/* Disabled until the fetch settles AND returned at least one
            account -- a failed or empty load must not hand the user an
            empty listbox (#24385 review, R2); `can_submit` already gates on
            `bank_account_id`, so the CTA stays off too. */}
        <StyledSelect
          id="final-pay-bank-account"
          value={bank_account_id ?? ''}
          options={build_bank_account_options(bank_accounts)}
          placeholder={t('employees.final_pay.lk_bank_account_placeholder')}
          disabled={!bank_accounts_loaded || bank_accounts.length === 0}
          onChange={(next) => {
            set_bank_account_id(next);
          }}
        />
      </div>

      {failure && (
        <Alert
          variant={failure.kind === 'already_terminated' ? 'warning' : 'danger'}
          data-testid="final-pay-failure"
        >
          {failure.kind === 'already_terminated'
            ? t('employees.terminate.already_terminated_error')
            : failure.kind === 'entitlement_required'
              ? t('employees.terminate.entitlement_required_error')
              : failure.kind === 'validation'
                ? failure.message
                : t('employees.terminate.error_generic')}
        </Alert>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="danger"
          onClick={() => {
            void handle_settle();
          }}
          disabled={!can_submit}
          data-testid="final-pay-lk-settle"
        >
          {t('employees.final_pay.lk_settle_cta')}
        </Button>
      </div>
    </div>
  );
}

// ── Indonesia pesangon branch ──────────────────────────────────────────────

function IdPesangonBranch({
  employee_id,
  employee_name,
  draft,
  actions,
  onSuccess,
}: BranchProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [termination_reason, set_termination_reason] =
    React.useState<PesangonTerminationReason>('resignation');
  const [unused_leave_sen, set_unused_leave_sen] = React.useState(0);
  const [repatriation_sen, set_repatriation_sen] = React.useState(0);
  const [other_uph_sen, set_other_uph_sen] = React.useState(0);
  const [separation_pay_sen, set_separation_pay_sen] = React.useState(0);
  const [pesangon_expense_account_id, set_pesangon_expense_account_id] =
    React.useState('');
  const [pesangon_payable_account_id, set_pesangon_payable_account_id] =
    React.useState('');
  const [failure, set_failure] =
    React.useState<TerminationMutationFailure | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);

  const reason_options: StyledSelectOption<PesangonTerminationReason>[] =
    PESANGON_REASON_OPTIONS.map((r) => ({
      value: r,
      label: pesangon_reason_label(t, r),
    }));

  async function handle_submit(): Promise<void> {
    set_failure(null);
    if (!actions.post_pesangon) return;
    if (
      pesangon_expense_account_id.trim() === '' ||
      pesangon_payable_account_id.trim() === ''
    ) {
      return;
    }
    set_is_submitting(true);
    const input: PostPesangonInput = {
      employee_id,
      termination_date: draft.termination_date,
      termination_reason,
      unused_leave_sen,
      repatriation_sen,
      other_uph_sen,
      separation_pay_sen,
      pesangon_expense_account_id: pesangon_expense_account_id.trim(),
      pesangon_payable_account_id: pesangon_payable_account_id.trim(),
    };
    const result = await actions.post_pesangon(input);
    set_is_submitting(false);
    if (result.outcome === 'failure') {
      set_failure(result.failure);
      return;
    }
    toast.success(
      t('employees.final_pay.id_pesangon_success', { name: employee_name }),
    );
    onSuccess();
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-[color:var(--ink-muted)]">
        {t('employees.final_pay.id_pesangon_heading')}
      </div>
      <div className="space-y-1">
        <label htmlFor="pesangon-reason" className="nv-label">
          {t('employees.final_pay.id_termination_reason_label')}
        </label>
        {/* `StyledSelect<PesangonTerminationReason>` carries the
            discriminated union through `onChange` itself -- no runtime
            re-narrowing needed, the options list is typed against it. */}
        <StyledSelect<PesangonTerminationReason>
          id="pesangon-reason"
          value={termination_reason}
          options={reason_options}
          onChange={set_termination_reason}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-unused-leave" className="nv-label">
          {t('employees.final_pay.id_unused_leave_label')}
        </label>
        <MoneyInput
          id="pesangon-unused-leave"
          className="w-full"
          value={unused_leave_sen}
          onChange={set_unused_leave_sen}
        />
        <p className="nv-help">
          {t('employees.final_pay.id_unused_leave_help')}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-repatriation" className="nv-label">
          {t('employees.final_pay.id_repatriation_label')}
        </label>
        <MoneyInput
          id="pesangon-repatriation"
          className="w-full"
          value={repatriation_sen}
          onChange={set_repatriation_sen}
        />
        <p className="nv-help">
          {t('employees.final_pay.id_repatriation_help')}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-other-uph" className="nv-label">
          {t('employees.final_pay.id_other_uph_label')}
        </label>
        <MoneyInput
          id="pesangon-other-uph"
          className="w-full"
          value={other_uph_sen}
          onChange={set_other_uph_sen}
        />
        <p className="nv-help">{t('employees.final_pay.id_other_uph_help')}</p>
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-separation-pay" className="nv-label">
          {t('employees.final_pay.id_separation_pay_label')}
        </label>
        <MoneyInput
          id="pesangon-separation-pay"
          className="w-full"
          value={separation_pay_sen}
          onChange={set_separation_pay_sen}
        />
        <p className="nv-help">
          {t('employees.final_pay.id_separation_pay_help')}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-expense-account" className="nv-label">
          {t('employees.final_pay.id_expense_account_label')}
        </label>
        <input
          id="pesangon-expense-account"
          className="nv-input w-full"
          type="text"
          value={pesangon_expense_account_id}
          onChange={(e) => {
            set_pesangon_expense_account_id(e.target.value);
          }}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="pesangon-payable-account" className="nv-label">
          {t('employees.final_pay.id_payable_account_label')}
        </label>
        <input
          id="pesangon-payable-account"
          className="nv-input w-full"
          type="text"
          value={pesangon_payable_account_id}
          onChange={(e) => {
            set_pesangon_payable_account_id(e.target.value);
          }}
        />
      </div>

      {failure && (
        <Alert
          variant={failure.kind === 'already_terminated' ? 'warning' : 'danger'}
          data-testid="final-pay-failure"
        >
          {failure.kind === 'already_terminated'
            ? t('employees.terminate.already_terminated_error')
            : failure.kind === 'entitlement_required'
              ? t('employees.terminate.entitlement_required_error')
              : failure.kind === 'validation'
                ? failure.message
                : t('employees.terminate.error_generic')}
        </Alert>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="danger"
          onClick={() => {
            void handle_submit();
          }}
          disabled={
            is_submitting ||
            pesangon_expense_account_id.trim() === '' ||
            pesangon_payable_account_id.trim() === ''
          }
          data-testid="final-pay-id-submit"
        >
          {t('employees.final_pay.id_submit_cta')}
        </Button>
      </div>
    </div>
  );
}

// ── OTHER jurisdiction branch (plain terminate, no settlement) ─────────────

function OtherJurisdictionBranch({
  is_contractor,
  employee_id,
  employee_name,
  draft,
  actions,
  onSuccess,
}: BranchProps & { is_contractor: boolean }): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [failure, set_failure] =
    React.useState<TerminationMutationFailure | null>(null);
  const [is_submitting, set_is_submitting] = React.useState(false);

  async function handle_confirm(): Promise<void> {
    set_failure(null);
    if (!actions.terminate) return;
    set_is_submitting(true);
    const result = await actions.terminate({
      employee_id,
      termination_date: draft.termination_date,
      reason: draft.reason,
    });
    set_is_submitting(false);
    if (result.outcome === 'failure') {
      set_failure(result.failure);
      return;
    }
    toast.success(t('employees.terminate.success', { name: employee_name }));
    onSuccess();
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[color:var(--ink-muted)]">
        {is_contractor
          ? t('employees.final_pay.contractor_note')
          : t('employees.final_pay.other_jurisdiction_note')}
      </p>
      <div className="glass-soft rounded-md px-4 py-3 text-sm">
        <div className="flex justify-between">
          <span className="text-[color:var(--ink-muted)]">
            {t('employees.terminate.date_label')}
          </span>
          <span className="num">{draft.termination_date}</span>
        </div>
        {draft.reason && (
          <div className="pt-1">
            <span className="text-[color:var(--ink-muted)] block">
              {t('employees.terminate.reason_label')}
            </span>
            <span>{draft.reason}</span>
          </div>
        )}
      </div>
      {failure && (
        <Alert
          variant={failure.kind === 'already_terminated' ? 'warning' : 'danger'}
        >
          {failure.kind === 'already_terminated'
            ? t('employees.terminate.already_terminated_error')
            : failure.kind === 'entitlement_required'
              ? t('employees.terminate.entitlement_required_error')
              : failure.kind === 'validation'
                ? failure.message
                : t('employees.terminate.error_generic')}
        </Alert>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="danger"
          onClick={() => {
            void handle_confirm();
          }}
          disabled={is_submitting}
          data-testid="final-pay-other-confirm"
        >
          {t('employees.terminate.confirm_cta')}
        </Button>
      </div>
    </div>
  );
}
